import { useCallback, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { Outlet } from "react-router-dom";

// material-ui
import { styled, useTheme, type Theme } from "@mui/material/styles";
import { Box, CssBaseline, useMediaQuery } from "@mui/material";

// project imports
import Header from "../../layout/MainLayout/Header";
import Sidebar from "./Sidebar";
import { drawerWidth } from "../../store/constant";
import { SET_DESKTOP_DRAWER, SET_MOBILE_DRAWER } from "../../store/actions";

interface MainProps {
  open: boolean;
  theme: Theme;
  // Add any other props used in the component here
}
// styles
const Main: any = styled("main", {
  shouldForwardProp: (prop) => prop !== "open"
})<MainProps | any>(({ theme, open }) => ({
  ...theme.typography.mainContent,
  ...(open === false && {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    [theme.breakpoints.up("md")]: {
      marginLeft: -(drawerWidth - 20),
      width: `calc(100% - ${drawerWidth}px)`
    },
    [theme.breakpoints.down("md")]: {
      marginLeft: "20px",
      width: `calc(100% - ${drawerWidth}px)`,
      padding: "16px"
    },
    [theme.breakpoints.down("sm")]: {
      marginLeft: "10px",
      width: `calc(100% - ${drawerWidth}px)`,
      padding: "16px",
      marginRight: "10px"
    }
  }),
  ...(open === true && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginLeft: 0,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    width: `calc(100% - ${drawerWidth}px)`,
    [theme.breakpoints.down("md")]: {
      marginLeft: "20px"
    },
    [theme.breakpoints.down("sm")]: {
      marginLeft: "10px"
    }
  })
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const matchDownMd = useMediaQuery(theme.breakpoints.down("md"));

  // Drawer states
  const desktopOpen = useAppSelector((state) => state.customization.desktopOpen) as boolean;
  const mobileOpen = useAppSelector((state) => state.customization.mobileOpen) as boolean;

  const handleLeftDrawerToggle = useCallback((): void => {
    if (matchDownMd) {
      dispatch({ type: SET_MOBILE_DRAWER, mobileOpen: !mobileOpen });
    } else {
      dispatch({ type: SET_DESKTOP_DRAWER, desktopOpen: !desktopOpen });
    }
  }, [dispatch, matchDownMd, mobileOpen, desktopOpen]);

  useEffect(() => {
    if (matchDownMd) {
      dispatch({ type: SET_MOBILE_DRAWER, mobileOpen: false });
    }
  }, [matchDownMd]);

  // const history = useNavigate()
  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      {/* header */}
      <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />

      {/* drawer */}
      <Sidebar drawerToggle={handleLeftDrawerToggle} />

      <Main theme={theme} open={matchDownMd ? mobileOpen : desktopOpen}>
        {/* main content */}
        {/* <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign /> */}
        {<Outlet />}
      </Main>
    </Box>
  );
};

export default MainLayout;
